/**
 * PLP product card — sibling swatch click loads another product card via ?view=product-card.
 */
class ProductCard extends HTMLElement {
    constructor() {
        super();
        this.isCollection = this.dataset.isCollection === 'true';
        this.isLoading = false;
        this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
        // Capture phase: must run before listeners bound directly on the swatch anchors (see onClick).
        this.addEventListener('click', this.onClick, { capture: true });
        this.unavailableIconDisabled();
        this.selectInitialSwatch();
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.onClick, { capture: true });
    }

    /**
     * Swatch clicks swap the card in place instead of opening the sibling PDP.
     * Third-party listeners bound on the swatch anchors (e.g. clerk.js product click tracking)
     * may navigate to the href regardless of preventDefault, so the event is stopped in capture
     * phase before reaching the anchor.
     *
     * @param {MouseEvent} event
     */
    onClick(event) {
        const swatch = event.target instanceof Element
            ? event.target.closest('.color-swatch-img-wrapper')
            : null;

        if (!swatch || !this.contains(swatch)) return;

        event.stopPropagation();

        // Modifier clicks keep the native link behavior (e.g. open sibling PDP in a new tab).
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        event.preventDefault();
        this.changeProduct(swatch);
    }

    selectInitialSwatch() {
        if (!this.isCollection) return;

        const newSwatch = Array.from(this.querySelectorAll('[data-swatch-color]')).find((swatch) => (
            swatch.dataset.variantLabelNew === 'true'
            && swatch.dataset.variantAvailable === 'true'
        ));

        if (newSwatch && !newSwatch.classList.contains('tw-color-swatch--active')) {
            this.changeProduct(newSwatch);
        }
    }

    /**
     * @param {HTMLElement} swatch
     */
    async changeProduct(swatch) {
        if (this.isLoading || swatch.classList.contains('tw-color-swatch--active')) return;

        const siblingUrl = swatch.dataset.siblingUrl;
        const siblingProductId = swatch.dataset.siblingProductId;

        if (!siblingUrl || !siblingProductId) return;

        this.isLoading = true;
        this.classList.add('product-card--loading');

        try {
            const response = await fetch(this.buildCardUrl(siblingUrl));

            if (!response.ok) {
                throw new Error(`Product card request failed (${response.status})`);
            }

            const html = await response.text();
            const fetchedCard = this.extractCardElement(html);

            if (!fetchedCard) return;

            this.applyCardUpdate(fetchedCard);
            this.dataset.clerkProductId = siblingProductId;
            this.setActiveSwatch(siblingProductId);
            this.unavailableIconDisabled();
        } catch (error) {
            console.error('ProductCard: sibling swap failed', error);
        } finally {
            this.isLoading = false;
            this.classList.remove('product-card--loading');
        }
    }

    /**
     * @param {string} productUrl
     * @returns {string}
     */
    buildCardUrl(productUrl) {
        const baseUrl = productUrl.startsWith('http')
            ? productUrl
            : `${window.shopUrl || window.location.origin}${productUrl}`;
        const separator = baseUrl.includes('?') ? '&' : '?';

        return `${baseUrl}${separator}view=product-card`;
    }

    /**
     * @param {string} html
     * @returns {HTMLElement | null}
     */
    extractCardElement(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        return doc.querySelector('.product-card-wrapper');
    }

    /**
     * Replaces card content from the fetched sibling, keeping the existing swatch row order.
     * @param {HTMLElement} fetchedCard
     */
    applyCardUpdate(fetchedCard) {
        const currentCard = this.querySelector('.product-card-wrapper');

        if (!currentCard) {
            this.innerHTML = fetchedCard.outerHTML;
            return;
        }

        const currentSwatches = currentCard.querySelector('fieldset.swatches');
        currentCard.innerHTML = fetchedCard.innerHTML;

        const fetchedSwatches = currentCard.querySelector('fieldset.swatches');

        if (currentSwatches && fetchedSwatches) {
            fetchedSwatches.replaceWith(currentSwatches);
        }
    }

    /**
     * @param {string} productId
     */
    setActiveSwatch(productId) {
        this.querySelectorAll('.color-swatch-img-wrapper').forEach((swatch) => {
            const isActive = swatch.dataset.siblingProductId === productId;
            swatch.classList.toggle('tw-color-swatch--active', isActive);

            if (isActive) {
                swatch.setAttribute('aria-current', 'page');
            } else {
                swatch.removeAttribute('aria-current');
            }
        });
    }

    unavailableIconDisabled() {
        this.querySelectorAll('.color-swatch-img-wrapper').forEach((colorCard) => {
            colorCard.style.opacity = colorCard.getAttribute('data-variant-available') === 'false' ? '0.3' : '1';
        });
    }
}

if (!customElements.get('product-card')) {
    customElements.define('product-card', ProductCard);
}
